import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, matchPath } from 'react-router-dom';
import { Trans } from 'react-i18next';

import { urlApplicationRoot } from '/business/shared/util/Statics';

import getApplicationIdSelector from '/business/shared/state/selectors/application/getApplicationIdSelector';
import getRequestedInformationLookupSelector from '/business/shared/state/selectors/application/initiatives/getRequestedInformationLookupSelector';
import getAttachedInitiativesSelector from '/business/shared/state/selectors/application/initiatives/getAttachedInitiativesSelector';
import getSiblingApplicationsSelector from '/business/shared/state/selectors/application/model/getSiblingApplicationsSelector';
import getLatestPackageApplicationIdSelector from '/business/shared/state/selectors/application/model/getLatestPackageApplicationIdSelector';
import getApplicationPackageComparison from '/business/shared/state/actions/applications/getApplicationPackageComparison';

import useScroll from '/shared/components/functional/useScroll';
import useUrlBuilder from '/business/shared/hooks/useUrlBuilder';

import LinkButton from '/shared/components/widgets/button/LinkButton';
import type { AppDispatch } from '/business/shared/state/store';

import nav, { type ReduceNavItem } from './nav';
import StyledReducePageHeaderNav, {
  StyledReduceHeaderCounter,
  StyledReduceHeaderCounterContainer,
  StyledReduceHeaderElement,
  StyledReduceHeaderItem,
  StyledReduceHeaderItems,
  StyledReduceHeaderName,
} from '/business/shared/components/pages/reduce/v3/nav/__styled__/StyledReduceHeaderNav';

function ReduceHeaderNav() {
  const dispatch = useDispatch<AppDispatch>();
  const applicationId = useSelector(getApplicationIdSelector);
  const attachedInitiatives = useSelector(getAttachedInitiativesSelector);
  const requestedInformationLookup = useSelector(getRequestedInformationLookupSelector);
  const siblingApplications = useSelector(getSiblingApplicationsSelector);
  const latestPackageApplicationId = useSelector(getLatestPackageApplicationIdSelector);
  const { getApplicationUrl } = useUrlBuilder();

  const { pathname } = useLocation();

  useScroll();

  const hasMultipleReportingPeriods = siblingApplications && siblingApplications.length > 1;
  const filteredNav = hasMultipleReportingPeriods ? nav : nav.filter((item) => item.key !== 'comparison');

  useEffect(() => {
    if (hasMultipleReportingPeriods) {
      dispatch(getApplicationPackageComparison());
    }
  }, [dispatch, hasMultipleReportingPeriods]);

  const getRequestsCounter = (navItem: ReduceNavItem) => {
    if (navItem?.key === 'request') {
      if (requestedInformationLookup) {
        const filtered = requestedInformationLookup.filter((item) => item.received !== false);
        return filtered?.length;
      }
      return 0;
    }
    if (navItem?.key === 'plan') {
      if (attachedInitiatives) {
        return attachedInitiatives?.length;
      }
      return 0;
    }
    return null;
  };

  return (
    <StyledReducePageHeaderNav>
      <StyledReduceHeaderItems>
        {filteredNav.map((navItem) => {
          const isHighlighted = matchPath(`/${urlApplicationRoot}/reduce/${navItem.url}`, pathname);
          const navApplicationId = navItem.key === 'targets' ? latestPackageApplicationId : applicationId;
          return (
            <StyledReduceHeaderItem key={navItem.key}>
              <LinkButton
                key={navItem.key}
                testId={`reduce-nav-${navItem.key}`}
                to={getApplicationUrl(`/reduce/${navItem.url}`, navApplicationId)}
              >
                <StyledReduceHeaderElement $isActive={Boolean(isHighlighted)}>
                  <StyledReduceHeaderName $isActive={Boolean(isHighlighted)}>
                    <Trans i18nKey={`business:ReducePageV3.${navItem.key}.navTitle`} />
                  </StyledReduceHeaderName>
                  {getRequestsCounter(navItem) !== null && (
                    <StyledReduceHeaderCounterContainer>
                      <StyledReduceHeaderCounter>{getRequestsCounter(navItem)}</StyledReduceHeaderCounter>
                    </StyledReduceHeaderCounterContainer>
                  )}
                </StyledReduceHeaderElement>
              </LinkButton>
            </StyledReduceHeaderItem>
          );
        })}
      </StyledReduceHeaderItems>
    </StyledReducePageHeaderNav>
  );
}

export default ReduceHeaderNav;
